import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Maximize2, X } from "lucide-react";
import {
    getProject,
    getProjects,
    getFileServeUrl,
    getSiteImages,
} from "./lib/g4Api";
import SEO from "../../components/g4/SEO";
import { G4Navbar, G4Footer } from "../../components/g4/Chrome";

const FALLBACK_IMGS = [
    "https://images.unsplash.com/photo-1726471760435-1646fa0c02ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc0ODY3NzM3fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1678110433739-53a9e5fb009b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc0ODY3NzM3fDA&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/4352247/pexels-photo-4352247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const API_BASE = "";
function resolveUrl(url) {
    if (!url) return "";
    if (url.startsWith("/api/")) return `${API_BASE}${url}`;
    return url;
}

function Navbar() {
    // Thin wrapper kept so the JSX call sites (<Navbar />) still work;
    // actual nav is the shared G4Navbar in the "always-white" variant.
    return <G4Navbar />;
}

export default function ProjectDetail() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [allProjects, setAllProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [siteImages, setSiteImages] = useState({});

    useEffect(() => {
        setLoading(true);
        Promise.all([getProject(projectId), getProjects(), getSiteImages()])
            .then(([proj, all, imgs]) => {
                setProject(proj);
                setAllProjects(all);
                setSiteImages(imgs);
                setLoading(false);
                window.scrollTo(0, 0);
            })
            .catch(() => {
                navigate("/");
            });
    }, [projectId, navigate]);

    const handleKeyDown = useCallback(
        (e) => {
            if (!lightboxOpen) return;
            if (e.key === "ArrowLeft" && lightboxIndex > 0) {
                setLightboxIndex((i) => i - 1);
            } else if (e.key === "ArrowRight") {
                setLightboxIndex((i) => i + 1);
            } else if (e.key === "Escape") {
                setLightboxOpen(false);
            }
        },
        [lightboxOpen, lightboxIndex]
    );

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const touchStartX = useRef(null);
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const threshold = 50;
        if (dx > threshold && lightboxIndex > 0) {
            setLightboxIndex((i) => i - 1);
        } else if (dx < -threshold) {
            setLightboxIndex((i) => i + 1);
        }
        touchStartX.current = null;
    };

    if (loading || !project) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-sm text-[#525252]">Loading project...</p>
            </div>
        );
    }

    const portfolioImgs = [
        resolveUrl(siteImages.portfolio_1),
        resolveUrl(siteImages.portfolio_2),
        resolveUrl(siteImages.portfolio_3),
    ].filter(Boolean);
    const fallbackPool = portfolioImgs.length > 0 ? portfolioImgs : FALLBACK_IMGS;
    const projectIndex = allProjects.findIndex((p) => p.id === project.id);
    const fallbackImg = fallbackPool[projectIndex >= 0 ? projectIndex % fallbackPool.length : 0];

    const uploadedImages = (project.images || []).filter(Boolean).map((id) => getFileServeUrl(id));
    const galleryImages = uploadedImages.length > 0 ? uploadedImages : [fallbackImg];
    const heroImage = galleryImages[0];

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const plainDesc = (project.description || "").replace(/<[^>]*>/g, "").substring(0, 160);

    return (
        <div data-testid="project-detail-page">
            <SEO
                title={project.title}
                description={`${project.title} — ${project.category} project in ${
                    project.location || "our portfolio"
                }. ${plainDesc}`}
                path={`/projects/${project.id}`}
                image={heroImage}
                type="article"
            />
            <Navbar />

            <section className="pt-28 md:pt-36">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Link
                        to="/#portfolio"
                        data-testid="back-to-portfolio"
                        className="inline-flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] mb-10 transition-colors"
                    >
                        <ArrowLeft size={16} /> Back to Projects
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10">
                        <div className="lg:col-span-3">
                            <div className="lg:sticky lg:top-28">
                                <h1
                                    data-testid="project-title"
                                    className="font-['Outfit'] text-3xl md:text-4xl tracking-tighter font-light text-[#0A0A0A] leading-[1.05] mb-6"
                                >
                                    {project.title}
                                </h1>
                                <div className="border-t border-[#E5E5E5] pt-5 space-y-3 mb-6">
                                    {project.category && (
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">
                                                Category
                                            </p>
                                            <p data-testid="project-category" className="text-sm text-[#525252]">
                                                {project.category}
                                            </p>
                                        </div>
                                    )}
                                    {project.location && (
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">
                                                Location
                                            </p>
                                            <p data-testid="project-location" className="text-sm text-[#525252]">
                                                {project.location}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="border-t border-[#E5E5E5] pt-6 mb-8">
                                    {project.description && project.description.includes("<") ? (
                                        <div
                                            data-testid="project-description"
                                            className="rich-content text-sm leading-relaxed text-[#525252] font-['Manrope']"
                                            dangerouslySetInnerHTML={{ __html: project.description }}
                                        />
                                    ) : (
                                        <p
                                            data-testid="project-description"
                                            className="text-sm leading-relaxed text-[#525252] font-['Manrope']"
                                        >
                                            {project.description}
                                        </p>
                                    )}
                                </div>
                                <Link
                                    to="/request-quote"
                                    data-testid="project-quote-btn"
                                    className="btn-studio w-full py-3 text-sm tracking-wide font-medium text-center block"
                                >
                                    Start a Similar Project
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-9">
                            {galleryImages.length > 0 && (
                                <div
                                    data-testid="project-gallery"
                                    className="columns-1 md:columns-2 gap-3 space-y-3"
                                >
                                    {galleryImages.map((img, i) => (
                                        <div
                                            key={i}
                                            className="relative group cursor-pointer overflow-hidden break-inside-avoid"
                                            onClick={() => openLightbox(i)}
                                        >
                                            <img
                                                src={img}
                                                alt={`${project.title} — ${i + 1}`}
                                                className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                                                <Maximize2
                                                    size={24}
                                                    className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <div className="py-12 md:py-20" />

            <G4Footer />

            {lightboxOpen && (
                <div
                    data-testid="lightbox-overlay"
                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
                    onClick={() => setLightboxOpen(false)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <button
                        data-testid="lightbox-close"
                        className="absolute top-6 right-6 text-white/70 hover:text-white z-10"
                        onClick={() => setLightboxOpen(false)}
                    >
                        <X size={28} />
                    </button>
                    {lightboxIndex > 0 && (
                        <button
                            data-testid="lightbox-prev"
                            className="absolute left-6 text-white/70 hover:text-white z-10"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightboxIndex((i) => i - 1);
                            }}
                        >
                            <ArrowLeft size={28} />
                        </button>
                    )}
                    {lightboxIndex < galleryImages.length - 1 && (
                        <button
                            data-testid="lightbox-next"
                            className="absolute right-6 text-white/70 hover:text-white z-10"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightboxIndex((i) => i + 1);
                            }}
                        >
                            <ArrowRight size={28} />
                        </button>
                    )}
                    <img
                        src={galleryImages[lightboxIndex]}
                        alt=""
                        className="max-w-[90vw] max-h-[85vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <p className="absolute bottom-6 text-sm text-white/50">
                        {lightboxIndex + 1} / {galleryImages.length}
                    </p>
                </div>
            )}
        </div>
    );
}
